import { useEffect, useRef, useState, type RefObject } from "react";

export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Adds `is-in` to elements with `data-reveal` inside `root` as they scroll into view. */
export function useReveal(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const container = root.current;
    if (!container) return;
    const targets = Array.from(container.querySelectorAll<HTMLElement>("[data-reveal]"));
    if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
      targets.forEach((target) => target.classList.add("is-in"));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            observer.unobserve(entry.target);
          }
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.12 },
    );
    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, [root]);
}

/** Pointer position within an element as CSS variables (--px, --py in 0..1), rAF-throttled. */
export function usePointerVars<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element || prefersReducedMotion() || window.matchMedia("(pointer: coarse)").matches) return;
    let frame = 0;
    const onMove = (event: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = element.getBoundingClientRect();
        element.style.setProperty("--px", ((event.clientX - rect.left) / rect.width).toFixed(3));
        element.style.setProperty("--py", ((event.clientY - rect.top) / rect.height).toFixed(3));
      });
    };
    const onLeave = () => {
      element.style.setProperty("--px", "0.5");
      element.style.setProperty("--py", "0.5");
    };
    element.addEventListener("pointermove", onMove);
    element.addEventListener("pointerleave", onLeave);
    return () => {
      cancelAnimationFrame(frame);
      element.removeEventListener("pointermove", onMove);
      element.removeEventListener("pointerleave", onLeave);
    };
  }, []);
  return ref;
}

/**
 * Steps through 0..count-1 with per-step durations, looping. Pauses while the
 * element is off-screen or the tab is hidden; reduced motion jumps to the end.
 */
export function useSequence(durations: readonly number[], visibleRef: RefObject<HTMLElement | null>) {
  const [step, setStep] = useState(() => (prefersReducedMotion() ? durations.length - 1 : 0));
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const element = visibleRef.current;
    if (!element || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.2 });
    observer.observe(element);
    return () => observer.disconnect();
  }, [visibleRef]);
  useEffect(() => {
    if (prefersReducedMotion() || !visible) return;
    const timer = window.setTimeout(() => {
      if (document.visibilityState === "visible") setStep((current) => (current + 1) % durations.length);
    }, durations[step]);
    return () => window.clearTimeout(timer);
  }, [step, visible, durations]);
  return step;
}
